// Propostas comerciais — o que servidor e cliente precisam compartilhar:
// catálogo de variáveis automáticas, rótulos de status e o modelo padrão.

export type ProposalStatus =
  | "draft"
  | "pending_approval"
  | "approved"
  | "rejected"
  | "sent"
  | "accepted"
  | "declined";

export const PROPOSAL_STATUS_LABELS: Record<ProposalStatus, string> = {
  draft: "Rascunho",
  pending_approval: "Aguardando aprovação",
  approved: "Aprovada",
  rejected: "Devolvida",
  sent: "Enviada ao cliente",
  accepted: "Aceita",
  declined: "Recusada",
};

export type ProposalVariableKind = "auto" | "ai" | "manual";

export type ProposalVariableDef = {
  key: string;
  label: string;
  kind: ProposalVariableKind;
  /** kind=auto: chave do catálogo (`AUTO_VARIABLES`) ou `meta.<campo>` da negociação. */
  source?: string;
  /** kind=ai: instrução para a IA escrever o campo. */
  prompt?: string;
  defaultValue?: string;
  multiline?: boolean;
};

/**
 * Variáveis que o sistema preenche sozinho a partir da negociação, do contato,
 * dos produtos, da empresa e do vendedor. `html: true` marca as que já vêm em
 * HTML (não escapar).
 */
export const AUTO_VARIABLES: ReadonlyArray<{
  key: string;
  label: string;
  group: string;
  html?: boolean;
}> = [
  { key: "proposta_numero", label: "Número da proposta", group: "Proposta" },
  { key: "proposta_data", label: "Data de emissão", group: "Proposta" },
  { key: "proposta_validade", label: "Válida até", group: "Proposta" },

  { key: "empresa_nome", label: "Nome da empresa", group: "Sua empresa" },
  { key: "empresa_cnpj", label: "CNPJ", group: "Sua empresa" },
  { key: "empresa_telefone", label: "Telefone", group: "Sua empresa" },
  { key: "empresa_endereco", label: "Endereço completo", group: "Sua empresa" },
  { key: "empresa_logo", label: "Logo (imagem)", group: "Sua empresa", html: true },
  { key: "empresa_cor", label: "Cor primária (hex)", group: "Sua empresa" },

  { key: "cliente_nome", label: "Nome do contato", group: "Cliente" },
  { key: "cliente_email", label: "E-mail do contato", group: "Cliente" },
  { key: "cliente_telefone", label: "Telefone do contato", group: "Cliente" },
  { key: "cliente_cargo", label: "Cargo do contato", group: "Cliente" },
  { key: "cliente_organizacao", label: "Organização / órgão", group: "Cliente" },
  { key: "cliente_cidade", label: "Cidade", group: "Cliente" },
  { key: "cliente_uf", label: "UF", group: "Cliente" },

  { key: "negociacao_titulo", label: "Título da negociação", group: "Negociação" },
  { key: "negociacao_descricao", label: "Descrição da negociação", group: "Negociação" },
  { key: "negociacao_valor", label: "Valor (R$)", group: "Negociação" },
  { key: "negociacao_previsao", label: "Previsão de fechamento", group: "Negociação" },

  { key: "produtos_tabela", label: "Tabela de produtos (HTML)", group: "Produtos", html: true },
  { key: "produtos_lista", label: "Lista de produtos (texto)", group: "Produtos" },
  { key: "produtos_total", label: "Total dos produtos (R$)", group: "Produtos" },

  { key: "vendedor_nome", label: "Nome do vendedor", group: "Vendedor" },
  { key: "vendedor_email", label: "E-mail do vendedor", group: "Vendedor" },
  { key: "vendedor_cargo", label: "Cargo do vendedor", group: "Vendedor" },

  { key: "assinatura_gestor", label: "Assinatura do gestor (imagem)", group: "Aprovação", html: true },
  { key: "gestor_nome", label: "Nome de quem aprovou", group: "Aprovação" },
  { key: "aprovado_em", label: "Data da aprovação", group: "Aprovação" },
];

export const AUTO_VARIABLE_KEYS = new Set(AUTO_VARIABLES.map((v) => v.key));
export const HTML_VARIABLE_KEYS = new Set(AUTO_VARIABLES.filter((v) => v.html).map((v) => v.key));

/** Extrai as chaves `{{...}}` usadas num HTML (sem duplicatas, na ordem). */
export function extractTemplateKeys(html: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const re = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    const key = m[1];
    if (!seen.has(key)) {
      seen.add(key);
      out.push(key);
    }
  }
  return out;
}

// ─── Modelo padrão ───────────────────────────────────────────────────────────
// Um ponto de partida decente para quem ainda não tem modelo. O usuário edita o
// HTML à vontade; as variáveis `apresentacao`/`escopo` são escritas pela IA e
// `condicoes` é digitada pelo vendedor.

export const DEFAULT_PROPOSAL_TEMPLATE_VARIABLES: ProposalVariableDef[] = [
  {
    key: "apresentacao",
    label: "Apresentação / contexto",
    kind: "ai",
    multiline: true,
    prompt:
      "Escreva 2 parágrafos curtos apresentando a proposta ao cliente: reconheça a necessidade que ele trouxe (use o histórico da negociação) e diga como a solução responde a ela. Tom cordial e direto, sem promessas que não estejam no contexto.",
  },
  {
    key: "escopo",
    label: "Escopo da solução",
    kind: "ai",
    multiline: true,
    prompt:
      "Descreva em tópicos o escopo do que está sendo proposto, a partir dos produtos/serviços listados e do contexto da negociação. Um tópico por linha, começando com '• '. Não invente itens que não estejam nos produtos ou nas notas.",
  },
  {
    key: "condicoes",
    label: "Condições comerciais",
    kind: "manual",
    multiline: true,
    defaultValue: "Pagamento em até 30 dias após a emissão da nota fiscal.\nValores válidos até a data de validade desta proposta.",
  },
];

export const DEFAULT_PROPOSAL_TEMPLATE_HTML = `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<style>
  @page { size: A4; margin: 18mm 16mm; }
  * { box-sizing: border-box; }
  body { font-family: "Helvetica Neue", Arial, sans-serif; color: #1f2937; font-size: 11pt; line-height: 1.5; margin: 0; }
  .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid {{empresa_cor}}; padding-bottom: 12px; margin-bottom: 24px; }
  .header img { max-height: 56px; }
  .header .meta { text-align: right; font-size: 9.5pt; color: #6b7280; }
  h1 { font-size: 20pt; color: {{empresa_cor}}; margin: 0 0 4px; }
  h2 { font-size: 13pt; color: {{empresa_cor}}; margin: 22px 0 8px; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; }
  .subtitle { color: #6b7280; margin-bottom: 18px; }
  .box { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px 14px; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .label { font-size: 8.5pt; text-transform: uppercase; letter-spacing: .04em; color: #6b7280; }
  table.produtos { width: 100%; border-collapse: collapse; margin-top: 6px; }
  table.produtos th { text-align: left; font-size: 9pt; text-transform: uppercase; color: #6b7280; border-bottom: 2px solid #e5e7eb; padding: 6px 8px; }
  table.produtos td { padding: 8px; border-bottom: 1px solid #f3f4f6; vertical-align: top; }
  table.produtos td.num, table.produtos th.num { text-align: right; white-space: nowrap; }
  table.produtos tr.total td { font-weight: bold; border-top: 2px solid #e5e7eb; border-bottom: none; }
  .assinaturas { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 40px; }
  .assinatura { text-align: center; }
  .assinatura .linha { border-top: 1px solid #9ca3af; margin-top: 8px; padding-top: 6px; font-size: 9.5pt; }
  .assinatura img { max-height: 70px; }
  .rodape { margin-top: 30px; font-size: 8.5pt; color: #9ca3af; text-align: center; }
  p { margin: 0 0 10px; white-space: pre-line; }
</style>
</head>
<body>
  <div class="header">
    <div>{{empresa_logo}}</div>
    <div class="meta">
      <div><strong>Proposta {{proposta_numero}}</strong></div>
      <div>Emitida em {{proposta_data}}</div>
      <div>Válida até {{proposta_validade}}</div>
    </div>
  </div>

  <h1>Proposta Comercial</h1>
  <div class="subtitle">{{negociacao_titulo}}</div>

  <div class="grid">
    <div class="box">
      <div class="label">Preparada para</div>
      <div><strong>{{cliente_organizacao}}</strong></div>
      <div>{{cliente_nome}} — {{cliente_cargo}}</div>
      <div>{{cliente_email}} · {{cliente_telefone}}</div>
      <div>{{cliente_cidade}} / {{cliente_uf}}</div>
    </div>
    <div class="box">
      <div class="label">Apresentada por</div>
      <div><strong>{{empresa_nome}}</strong></div>
      <div>CNPJ {{empresa_cnpj}}</div>
      <div>{{vendedor_nome}} — {{vendedor_cargo}}</div>
      <div>{{vendedor_email}} · {{empresa_telefone}}</div>
    </div>
  </div>

  <h2>Apresentação</h2>
  <p>{{apresentacao}}</p>

  <h2>Escopo da solução</h2>
  <p>{{escopo}}</p>

  <h2>Investimento</h2>
  {{produtos_tabela}}

  <h2>Condições comerciais</h2>
  <p>{{condicoes}}</p>

  <div class="assinaturas">
    <div class="assinatura">
      {{assinatura_gestor}}
      <div class="linha"><strong>{{gestor_nome}}</strong><br>{{empresa_nome}}<br>{{aprovado_em}}</div>
    </div>
    <div class="assinatura">
      <div style="height:70px"></div>
      <div class="linha"><strong>{{cliente_nome}}</strong><br>{{cliente_organizacao}}<br>De acordo</div>
    </div>
  </div>

  <div class="rodape">{{empresa_nome}} · {{empresa_endereco}} · {{empresa_telefone}}</div>
</body>
</html>`;
